import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import PortalShell from "@/components/PortalShell";
import { Megaphone, Plus, Trash2, ArrowLeft } from "lucide-react";

export default function Board() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = ["staff", "admin"].includes(user?.role);
  const homePath = isStaff ? "/staff" : "/portal";
  const [ads, setAds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tag: "Announcement", image: "" });

  const load = async () => {
    try { const { data } = await api.get("/ads"); setAds(data); } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ads", form);
      toast.success("Posted to the board");
      setForm({ title: "", body: "", tag: "Announcement", image: "" });
      setShowForm(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => { try { await api.delete(`/ads/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const navItems = [
    { key: "back", label: isStaff ? "Back to office" : "Back to portal", icon: <ArrowLeft size={16} /> },
    { key: "board", label: "Notice board", icon: <Megaphone size={16} /> },
  ];

  return (
    <PortalShell nav={navItems} active="board" onNav={(k) => { if (k === "back") navigate(homePath); }} title="Notice board">
      <button className="link-u" onClick={() => navigate(homePath)} data-testid="board-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "var(--honey)", marginTop: -14, marginBottom: 16 }}>
        <ArrowLeft size={15} /> {isStaff ? "Back to office" : "Back to portal"}
      </button>
      <p style={{ color: "var(--muted)", marginBottom: 28, maxWidth: "56ch" }}>
        Seasonal reminders, offers and news for Solvana owners, the community advertising board.
      </p>

      {isStaff && (
        <div style={{ marginBottom: 28 }}>
          {!showForm ? (
            <button data-testid="new-ad-btn" className="btn-ink" onClick={() => setShowForm(true)}><Plus size={15} style={{ marginRight: 8 }} /> New post</button>
          ) : (
            <form onSubmit={post} className="card-soft" style={{ padding: 22, display: "grid", gap: 12, maxWidth: 620 }}>
              <input className="inp" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              <textarea className="inp" rows={3} placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required />
              <div style={{ display: "flex", gap: 12 }}>
                <select className="inp" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
                  {["Announcement", "Offer", "Seasonal", "News"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <input className="inp" placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button className="btn-ink" type="submit">Publish</button>
                <button className="btn-outline" type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
        {ads.map((a) => (
          <div key={a.ad_id} data-testid="ad-card" className="card-soft hover-lift" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {a.image && <img src={a.image} alt="" style={{ width: "100%", height: 170, objectFit: "cover" }} />}
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              <span className="lbl">{a.tag}</span>
              <h3 style={{ fontSize: 24 }}>{a.title}</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, flex: 1 }}>{a.body}</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--muted)", letterSpacing: ".06em" }}>
                <span>{a.author}</span>
                {isStaff && <button onClick={() => del(a.ad_id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }}><Trash2 size={14} /></button>}
              </div>
            </div>
          </div>
        ))}
        {ads.length === 0 && <p style={{ color: "var(--muted)" }}>No posts yet.</p>}
      </div>
    </PortalShell>
  );
}
